import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const alertVariants = cva("relative w-full rounded-lg border", {
  variants: {
    variant: {
      default: "bg-background text-foreground border-border",
      info: "bg-info/10 border-info/30 text-info",
      success: "bg-success/10 border-success/30 text-success",
      warning: "bg-warning/10 border-warning/30 text-warning",
      error: "bg-error/10 border-error/30 text-error",
    },
    size: {
      xs: "p-2",
      sm: "p-3",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "sm",
  },
});

const titleVariants = cva("font-semibold leading-none tracking-tight", {
  variants: {
    size: {
      xs: "text-xs mb-1",
      sm: "text-sm mb-2",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

const descriptionVariants = cva("[&_p]:leading-relaxed", {
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  icon?: React.ReactNode;
  title?: string;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, size, icon, title, children, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant, size }), className)}
      {...props}
    >
      <div className="flex items-center gap-2">
        {icon && <span className="shrink-0 self-start">{icon}</span>}
        <div className="min-w-0 flex-1">
          {title && <h5 className={cn(titleVariants({ size }))}>{title}</h5>}
          <div className={cn(descriptionVariants({ size }))}>{children}</div>
        </div>
      </div>
    </div>
  )
);
Alert.displayName = "Alert";

export { Alert };
